import { type ReactElement, useEffect, useState } from "react";

import { App } from "@/App";
import { useResource } from "@/lib/api";
import { type PlatformSession, loadPlatformSession } from "@/lib/platform";
import { PlatformApp, PlatformSignIn } from "@/platform/PlatformApp";

/**
 * `staff_login.failed` sends every failed sign-in back to where it started with
 * `?fehler=anmeldung`. On both surfaces that includes the fresh sign-in a write asks for
 * (401): the write was then not done, and without this sentence nobody would know.
 */
const SIGN_IN_FAILED =
  "Die Anmeldung ist fehlgeschlagen. Der letzte Schritt wurde nicht ausgeführt.";

/**
 * index.html names the tab for the tenant backoffice. An operator with both open side by
 * side has to tell from the tab which one migrates databases, sign-in page included.
 */
const PLATFORM_TITLE = "Plattform · Countdown-Tool";

/**
 * Which application this host serves: the platform administration or a tenant backoffice.
 *
 * One bundle for both, so the page asks the API once. The platform routes answer only on
 * the configured platform host (401 there without a session); every tenant host answers
 * 404, and so does anything unexpected, which falls through to the tenant backoffice as it
 * was. The frontend never guesses from the hostname: the platform host is configuration.
 *
 * The question costs one request that opens no database. Nothing renders until it is
 * answered, so neither application flashes on the other's host.
 */
export function Surface(): ReactElement | null {
  const session = useResource<PlatformSession>(loadPlatformSession);
  // Read once: the marker belongs to the page the sign-in returned to, not to every
  // page the operator opens after it.
  const [signInFailed] = useState(
    () => new URLSearchParams(window.location.search).get("fehler") === "anmeldung",
  );
  const platform =
    session.status === "ready" || session.status === "unauthenticated";
  useEffect(() => {
    if (platform) {
      document.title = PLATFORM_TITLE;
    }
  }, [platform]);

  if (session.status === "loading") {
    return null;
  }
  let surface: ReactElement;
  if (session.status === "ready") {
    surface = <PlatformApp session={session.data} />;
  } else if (session.status === "unauthenticated") {
    surface = <PlatformSignIn />;
  } else {
    surface = <App />;
  }
  return (
    <>
      {signInFailed && (
        <div className="mx-auto max-w-5xl px-4 pt-4">
          <p className="border border-destructive bg-destructive/5 px-4 py-3 text-sm text-destructive" role="alert">
            {SIGN_IN_FAILED}
          </p>
        </div>
      )}
      {surface}
    </>
  );
}
